import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { requestContext } from '../../../common/requestContext';
import useTypedDispatch from '../../../common/useTypedDispatch';
import useTypedSelector from '../../../common/useTypedSelector';
import { Icon } from '../../../design-system/atoms/Icon/Icon';
import { IconType } from '../../../design-system/atoms/Icon/IconType.enum';
import { Text } from '../../../design-system/atoms/Text/Text';
import { MobileLayoutTextViewerPreviewHeader } from '../../../design-system/layout/MobileLayoutTextViewerPreviewHeader';
import { PcLayoutTextViewerPreviewHeader } from '../../../design-system/layout/PcLayoutTextViewerPreviewHeader';
import { CmsTextViewerFetcherHeadless } from '../../../domain/cms/text-viewer-preview/headless/CmsTextViewerFetcherHeadless';
import { SLIDE_TO_ZERO_TEXT_VIEWER_EVENT_NAME } from '../../../domain/content/[seriesId]/viewer/[productId]/constant/viewerConstant';
import { TextViewerContainer } from '../../../domain/content/[seriesId]/viewer/[productId]/container/TextViewerContainer';
import { TextViewerPreviewSettingContainer } from '../../../domain/content/[seriesId]/viewer/[productId]/container/TextViewerPreviewSettingContainer';
import { TextViewerFetcherHeadless } from '../../../domain/content/[seriesId]/viewer/[productId]/headless/TextViewerFetcherHeadless';
import { changeReadType } from '../../../domain/content/[seriesId]/viewer/[productId]/state/textViewerSlice';
import { TextViewerReadType } from '../../../domain/content/[seriesId]/viewer/[productId]/type/TextViewerReadType';
import { ApiErrorBoundary } from '../../../errors/ApiErrorBoundary.class';
import { MOBILE_HEADER_HEIGHT_PX } from '../../../header/constants';
const CmsTextViewerPreview = () => {
  const router = useRouter();
  const previewKey = String(router.query?.k || '');
  const isProductId = router.query?.is_product_id === 'true';
  const previewInfo = useTypedSelector(state => state.viewerInfo.textViewerPreview[previewKey]?.data);
  const seriesId = useMemo(() => previewInfo?.seriesItem.seriesId, [previewInfo?.seriesItem.seriesId]);
  const productId = useMemo(() => previewInfo?.item.productId, [previewInfo?.item.productId]);
  const dispatch = useTypedDispatch();
  useEffect(() => {
    // 뷰어 설정 기본값 세팅을 여기서 합니다.
    dispatch(changeReadType(TextViewerReadType.Scroll));
  }, [dispatch]);
  const {
    isMobile
  } = requestContext.requestInfo.display;
  const {
    isViewerMenuExposed
  } = useTypedSelector(state => ({
    isViewerMenuExposed: state.viewerMenu.isExposed
  }));
  const [isPcSettingExposed, setIsPcSettingExposed] = useState(false);
  const handleClickPcSettingButton = useCallback(() => {
    setIsPcSettingExposed(true);
  }, []);
  const handleClickPcSettingClose = useCallback(() => {
    setIsPcSettingExposed(false);
  }, []);
  const textViewerSettings = useTypedSelector(state => state.textViewer.settings);
  const onClickPcGoToStartButton = useCallback(() => {
    window.dispatchEvent(new Event(SLIDE_TO_ZERO_TEXT_VIEWER_EVENT_NAME));
  }, []);
  if (!router.isReady) {
    return null;
  }
  return <div className="relative flex w-full grow flex-col items-center">
      <ApiErrorBoundary>
        <CmsTextViewerFetcherHeadless previewKey={previewKey} isProductId={isProductId}>
          {viewerInfo => <>
              {seriesId && productId && <>
                  {!isMobile && <>
                      <div className="fixed top-0 z-150 flex w-full justify-center bg-theme-bg-base">
                        <PcLayoutTextViewerPreviewHeader seriesId={seriesId} productId={productId} />
                      </div>
                      <div className="fixed right-40pxr bottom-40pxr z-100">
                        {/* TODO : 책갈피 기능이 추가되면 플로팅 버튼이 추가되어야합니다 */}
                        <div className="mb-10pxr flex h-48pxr w-48pxr cursor-pointer items-center justify-center rounded-full border-1 border-theme-transparent-20 bg-theme-bg-base" onClick={handleClickPcSettingButton}>
                          <Icon type={IconType.NavigationSettingEpub} disableActiveEffect />
                        </div>
                        <div className="flex h-48pxr w-48pxr cursor-pointer items-center justify-center rounded-full border-1 border-theme-transparent-20 bg-theme-bg-base" onClick={onClickPcGoToStartButton}>
                          <Icon type={textViewerSettings.readType === TextViewerReadType.Scroll ? IconType.ViewerTop : IconType.ViewerStart} disableActiveEffect />
                        </div>
                      </div>
                      {/* 뷰어 설정 Dialog(Mobile) or Floating(PC) */}
                      {!isMobile && isPcSettingExposed && <>
                          {/* 뷰어 설정 배경 */}
                          <div className="fixed top-0 left-0 z-100 h-full w-full" onClick={handleClickPcSettingClose} />
                          <div className="fixed right-40pxr bottom-40pxr z-150 w-360pxr rounded-16pxr bg-theme-bg-surface py-24pxr shadow-dialog dark:shadow-none">
                            <div className="mb-10pxr flex h-44pxr items-center justify-between pr-16pxr pl-24pxr">
                              <Text className="font-medium2-bold text-theme-solid-100">
                                뷰어 설정
                              </Text>
                              <Icon type={IconType.NavigationClose} className="cursor-pointer" onClick={handleClickPcSettingClose} />
                            </div>
                            <TextViewerPreviewSettingContainer productId={productId} />
                          </div>
                        </>}
                    </>}
                  {isMobile && <>
                      <div className="fixed top-0 z-150 flex w-full max-w-1200pxr flex-col items-center bg-theme-bg-base">
                        <MobileLayoutTextViewerPreviewHeader seriesId={seriesId} productId={productId} />
                      </div>
                      {/* 프리뷰에서는 정주행, 댓글 등의 기능이 없으므로 하단에 보여줄 메뉴가 없습니다.
                       텍스트뷰어 컨테이너 내부에 시크바 위치 등이 고정되어 있는데 해당 값을 바닥에 붙이는 방법도 있지만
                       실제 유저가 보는 화면과 컨텐츠 영역이 달라져 오해할 수 있을듯해 더미로 구현해둡니다. */}
                      {isViewerMenuExposed && <div className="fixed inset-x-0 bottom-0 z-150 bg-theme-bg-base" style={{
                height: MOBILE_HEADER_HEIGHT_PX
              }} />}
                    </>}
                  <TextViewerFetcherHeadless seriesId={seriesId} productId={productId} viewerInfoData={viewerInfo}>
                    <TextViewerContainer seriesId={seriesId} productId={productId} isCmsPreview={true} isPreview={false}>
                      <div className="font-large1 flex h-full w-full items-center justify-center bg-black text-white">
                        <div>뷰어엔드 자리입니다.</div>
                      </div>
                    </TextViewerContainer>
                  </TextViewerFetcherHeadless>
                </>}
            </>}
        </CmsTextViewerFetcherHeadless>
      </ApiErrorBoundary>
    </div>;
};
export default CmsTextViewerPreview;